import * as React from 'react';
import { useContext } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import { LinkIcon, HouseIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';

import { NavRoutes } from '@/components/sidenav/nav-routes';
import { TeamSwitcher } from '@/components/sidenav/team-switcher';
import { NavUser } from '@/components/sidenav/nav-user';
import { ViewModeToggle } from '@/components/sidenav/view-mode-toggle';
import { NotificationBell } from '@/components/sidenav/notification-bell';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { AuthContext } from '@/context/auth.context';

import routes from 'src/routes';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  brandName?: string;
}

export function AppSidebar({ brandName = 'App', ...props }: AppSidebarProps) {
  const { currentUser } = useContext(AuthContext);
  const isExternalShareUser = currentUser?.isExternalShareUser ?? false;
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <div className="flex items-center justify-between">
          <div className="relative flex items-center">
            <TeamSwitcher brandName={brandName} />
            {!isExternalShareUser && (
              <AnimatePresence initial={false}>
                {isCollapsed && (
                  <motion.div
                    key="notification-dot"
                    className="absolute -right-0.5 -top-0.5 z-10"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <NotificationBell variant="dot" />
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isExternalShareUser && (
              <AnimatePresence initial={false}>
                {!isCollapsed && (
                  <motion.div
                    key="notification-bell"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                  >
                    <NotificationBell />
                  </motion.div>
                )}
              </AnimatePresence>
            )}
            {!isExternalShareUser && !isCollapsed && <ViewModeToggle />}
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {isExternalShareUser ? (
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Shared Links">
                  <RouterLink to="/shared-links">
                    <LinkIcon className="h-4 w-4" />
                    <span>Shared Links</span>
                  </RouterLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Home">
                    <RouterLink to="/home">
                      <HouseIcon className="h-4 w-4" />
                      <span>Home</span>
                    </RouterLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
            <NavRoutes routes={routes} />
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
